import React, { useState, useEffect } from 'react'
import { Box, Button, Card, CardContent, Grid, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FormProvider from '../../../common/hook-form/FormProvider'
import { MerchantFilterCached } from '../../../common/ProshipFormComponents'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { apiConstants, post } from '../../../common/constants'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import CopyIcon from '../../../common/CopyIcon'

const PAGE_SIZE = 20

const ORDER_TYPE_COLOR = {
  'FORWARD SHIPMENT': 'primary',
  'REVERSE SHIPMENT': 'warning',
}

export default function ShipmentsTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(PAGE_SIZE)
  const [filters, setFilters] = useState(null)

  const methods = useForm({
    defaultValues: { merchant: null },
  })

  const { handleSubmit, reset } = methods

  const handleReset = () => {
    reset({ merchant: null })
    setRows([])
    setSubmitted(false)
    setPage(0)
    setFilters(null)
  }

  const onSubmit = (data) => {
    setPage(0)
    setFilters({ merchantId: data.merchant || undefined })
  }

  useEffect(() => {
    if (!filters) return
    setLoading(true)
    post(apiConstants.AUDIT_B2C_SHIPMENTS, { ...filters, offset: page * sizePerPage, limit: sizePerPage })
      .then((res) => {
        const raw = res?.data
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
        setRows(list.map((r, i) => ({
          ...r,
          id: i,
          courierName: r.courierDetail?.name || '—',
          courierParent: r.courierDetail?.parent || '—',
        })))
        setSubmitted(true)
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Failed to fetch shipment activity'
        enqueueSnackbar(msg, { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }, [filters, page, sizePerPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      field: 'awbRegisteredDate',
      headerName: 'Registered At',
      flex: 1.2,
      renderCell: (p) => (
        <Typography variant="body2">
          {p.value ? format(new Date(p.value), 'dd MMM yyyy, HH:mm') : '—'}
        </Typography>
      ),
    },
    {
      field: 'awbNumber',
      headerName: 'AWB',
      flex: 1.2,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {p.value || '—'}<CopyIcon text={p.value} />
        </Typography>
      ),
    },
    {
      field: 'reference',
      headerName: 'Reference',
      flex: 1.5,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'orderType',
      headerName: 'Order Type',
      flex: 1.2,
      renderCell: (p) =>
        p.value ? (
          <Chip
            label={p.value}
            size="small"
            color={ORDER_TYPE_COLOR[p.value] || 'default'}
            variant="outlined"
          />
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
    {
      field: 'courierName',
      headerName: 'Courier',
      flex: 1.5,
      renderCell: (p) => (
        <Tooltip title={p.row.courierParent || ''}>
          <Typography variant="body2">{p.value || '—'}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'labelUrl',
      headerName: 'Label',
      flex: 0.6,
      sortable: false,
      renderCell: (p) =>
        p.value ? (
          <Tooltip title="Download Label">
            <IconButton
              size="small"
              component="a"
              href={p.value}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
    {
      field: 'invoiceUrl',
      headerName: 'Invoice',
      flex: 0.6,
      sortable: false,
      renderCell: (p) =>
        p.value ? (
          <Tooltip title="Download Invoice">
            <IconButton
              size="small"
              component="a"
              href={p.value}
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
  ]

  return (
    <Box>
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>Search Filters</Typography>
        </Box>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <MerchantFilterCached fullWidth />
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="medium" disabled={loading} startIcon={<SearchOutlinedIcon />}>
                    Search
                  </Button>
                  <Button type="button" variant="outlined" size="medium" onClick={handleReset} startIcon={<RestartAltOutlinedIcon />}>
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {submitted && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" fontWeight={600}>Results</Typography>
            <Chip label={`${rows.length} records`} size="small" color="primary" variant="outlined" />
          </Box>
          {rows.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <InboxOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>No records found</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Try adjusting your filters and search again</Typography>
            </Box>
          ) : (
            <CardContent sx={{ px: 0, pb: '0 !important' }}>
              <ProzoDataGrid
                rows={rows}
                columns={columns}
                rowCount={Number.MAX_VALUE}
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                paginationMode="server"
                disableColumnMenu
                disableColumnFilter
              />
            </CardContent>
          )}
        </Card>
      )}
    </Box>
  )
}
